import {useState, useCallback} from "react";
import {ActionedRecord, BucketData, BucketFilters, ImbalanceItem} from "../data/types";
import {adjUnits} from "../data/imbalanceHelpers";

type UseBucketActionsResult = {
    bucketData: BucketData;
    bucketFilters: BucketFilters;
    actionedCodes: Record<string, ActionedRecord>;
    totalBucketCount: number;
    setBucketFilter: (key: 'zero' | 'under' | 'over', val: string) => void;
    suggestCleanups: () => void;
    removeRecord: (key: 'zero' | 'under' | 'over', idx: number) => void;
    performAction: (key: 'zero' | 'under' | 'over') => void;
};

export function useBucketActions(
    imbalances: ImbalanceItem[],
    onSwitchToCleanups: () => void,
): UseBucketActionsResult {
    const [bucketData, setBucketData] = useState<BucketData>({zero: [], under: [], over: []});
    const [bucketFilters, setBucketFilters] = useState<BucketFilters>({zero: '', under: '', over: ''});
    const [actionedCodes, setActionedCodes] = useState<Record<string, ActionedRecord>>({});

    const totalBucketCount = bucketData.zero.length + bucketData.under.length + bucketData.over.length;

    const setBucketFilter = useCallback((key: 'zero' | 'under' | 'over', val: string) => {
        setBucketFilters(prev => ({...prev, [key]: val}));
    }, []);

    const suggestCleanups = useCallback(() => {
        const eligible = imbalances.filter(item =>
            item.persistentImbalance !== 0 &&
            !actionedCodes[item.material]
        );

        const top1k = (arr: ImbalanceItem[]) =>
            [...arr].sort((a, b) => adjUnits(b) - adjUnits(a)).slice(0, 1000);

        setBucketData({
            zero: top1k(eligible.filter(item => item.direction === 'WMS_0')),
            under: top1k(eligible.filter(item => item.direction === 'WMS_LESS_THAN_CI')),
            over: top1k(eligible.filter(item => item.direction === 'WMS_MORE_THAN_CI')),
        });
        setBucketFilters({zero: '', under: '', over: ''});
        onSwitchToCleanups();
    }, [imbalances, actionedCodes, onSwitchToCleanups]);

    const removeRecord = useCallback((key: 'zero' | 'under' | 'over', idx: number) => {
        setBucketData(prev => {
            const updated = {...prev};
            updated[key] = [...prev[key]];
            updated[key].splice(idx, 1);
            return updated;
        });
    }, []);

    const performAction = useCallback((key: 'zero' | 'under' | 'over') => {
        const adjType = key === 'over' ? 'POSADJ' : 'NEGADJ';
        const lcLabel = key === 'zero' ? 'LC: Yes' : key === 'under' ? 'LC: No' : 'Bonded';

        setActionedCodes(prev => {
            const next = {...prev};
            bucketData[key].forEach(item => {
                next[item.material] = {status: 'processing', adjType, units: adjUnits(item), lc: lcLabel, bucket: key};
            });
            return next;
        });

        setTimeout(() => {
            const ref = `${adjType}-${Date.now().toString(36).toUpperCase().slice(-6)}`;
            const ts = new Date().toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
            });

            setActionedCodes(prev => {
                const next = {...prev};
                bucketData[key].forEach(item => {
                    if (next[item.material]) {
                        next[item.material] = {...next[item.material], status: 'processed', ref, ts};
                    }
                });
                return next;
            });
        }, 1400);
    }, [bucketData]);

    return {
        bucketData, bucketFilters, actionedCodes, totalBucketCount,
        setBucketFilter, suggestCleanups, removeRecord, performAction,
    };
}
